import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

/**
 * Tokens + formatters for the assistant's dashboard render blocks.
 *
 * Split of responsibility (see `references/design-palette.md`):
 *
 * - **Chrome** — surfaces, hairlines, text, axis labels, pill fills — comes from the
 *   admin portal's own MUI theme, so a chart sits inside the portal instead of beside
 *   it, and light/dark come for free.
 * - **Marks** — bars, lines, slices, sparklines — keep the validated categorical
 *   palette below. These hexes are *computed*, not picked: they clear an OKLCH
 *   lightness band, a chroma floor, colour-vision-deficiency separation, a
 *   normal-vision separation floor and surface contrast, in both modes
 *   simultaneously. The portal's brand ramp (a deep navy) sits below the chroma floor
 *   and reads as grey in a small mark, which is exactly why it is not reused here.
 *
 * Do not reorder `SERIES` and do not hand-tweak these values; both are
 * re-validation events. `SERIES` is assigned in order and never cycled — past five
 * categories the answer is a table, not a sixth colour.
 *
 * SERIES[0] and SERIES[4] land below 3:1 against their surface. That is only
 * acceptable with a redundant channel, which is why every chart here ships direct
 * value labels and a "Show data as table" toggle.
 */

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/** Single-series / nominal magnitude hue. Never colour nominal bars by value. */
export const PRIMARY_MARK = '#375fbe';
/** Lighter step of the same ramp — meter tracks and de-emphasised marks. */
export const PRIMARY_SOFT_MARK = '#c7d3ee';
/** The "everything else" hue for the emphasis form (`highlight`). */
export const MUTED_MARK = '#b8bfcc';

/** Categorical slots, assigned in order and never cycled. */
export const SERIES = [
  '#07b061', // 1 green
  '#0a51f5', // 2 blue
  '#b07207', // 3 amber
  '#099de1', // 4 cyan
  '#b11010', // 5 red
] as const;

/**
 * The status scale is reserved: never reused as "series N", always shipped with an
 * icon *and* a label. A reviewer who has learned red means failure will read a red
 * mark as failure wherever it appears.
 */
export const STATUS_MARK: Record<StatusTone, string> = {
  success: '#20b16d',
  warning: '#f59f0a',
  danger: '#dc2828',
  info: '#0da2e7',
  neutral: '#6b7280',
};

export type VizChrome = {
  /** Grid + axis rules. Hairline, solid, never dashed. */
  grid: string;
  /** Axis tick text. */
  axis: string;
  /** Value labels and titles. */
  ink: string;
  /** The surface doing the separating (donut gaps, marker rings). */
  surface: string;
  /** Unfilled meter track. */
  track: string;
};

/** Surface-relative chrome, pulled from the portal theme so both modes match the app. */
export function vizChrome(theme: Theme): VizChrome {
  const isLight = theme.palette.mode === 'light';
  return {
    grid: theme.palette.divider,
    axis: theme.palette.text.secondary,
    ink: theme.palette.text.primary,
    surface: theme.palette.background.paper,
    track: alpha(theme.palette.text.primary, isLight ? 0.08 : 0.18),
  };
}

/**
 * Pill fill + text for a status tone, from the theme's own state colours.
 *
 * Text uses the `dark` step in light mode and the `light` step in dark mode — the
 * `main` steps are tuned as fills and fail text contrast over a tinted background.
 */
export function statusPillColors(theme: Theme, tone: StatusTone): { bg: string; fg: string } {
  const isLight = theme.palette.mode === 'light';
  if (tone === 'neutral') {
    return {
      bg: alpha(theme.palette.text.primary, isLight ? 0.07 : 0.16),
      fg: theme.palette.text.secondary,
    };
  }
  const family =
    tone === 'success'
      ? theme.palette.success
      : tone === 'warning'
        ? theme.palette.warning
        : tone === 'danger'
          ? theme.palette.error
          : theme.palette.info;
  return {
    bg: alpha(family.main, isLight ? 0.14 : 0.26),
    fg: isLight ? family.dark : family.light,
  };
}

/**
 * Map free text onto the reserved status scale.
 *
 * Four ordered families, **danger first** so "not passed" cannot land in the success
 * family. Extended with the loan-ops vocabulary the assistant actually emits
 * (ABANDONED, EXPIRED, STUCK, DECLINED, …).
 */
export function toneForLabel(raw: string): StatusTone {
  const v = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');
  if (!v) return 'neutral';
  if (
    /(^|\b)(fail|failed|failure|error|critical|reject|rejected|declin|denied|overdue|fatal|risk|abandon|abandoned|expired|cancel|cancelled|canceled|stuck|blocked|fraud|charge.?off|default)/.test(
      v,
    )
  ) {
    return 'danger';
  }
  if (
    /(^|\b)(pass|passed|complete|completed|ok|approved|resolved|done|success|verified|funded|settled|clear|cleared|match|matched)/.test(
      v,
    )
  ) {
    return 'success';
  }
  if (
    /(^|\b)(pending|warning|warn|review|manual|at.?risk|processing|in.?progress|escalat|hold|waiting|partial|retry|stale|incomplete)/.test(
      v,
    )
  ) {
    return 'warning';
  }
  if (/(^|\b)(info|notify|note|draft|new|submitted|started|active|open)\b/.test(v)) return 'info';
  return 'neutral';
}

function trimTo1dp(v: number): string {
  return (Math.round(v * 10) / 10).toString();
}

/**
 * Compact figures for counts.
 *
 * Only past a **million**. A reported figure is the answer to the operator's question,
 * and `16.7K` for 16,739 or `1.2K` for 1,233 silently deletes the digits they came for —
 * the tile then contradicts the prose in the same reply. Six digits still fit a tile at
 * this type size, so there is nothing to buy by rounding them away.
 */
export function compact(n: number): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${trimTo1dp(v / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${trimTo1dp(v / 1_000_000)}M`;
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * Axis ticks are the exception: they are scale references, not reported figures, and
 * the left gutter is 42px wide — so they keep the K step.
 */
export function axisTick(n: number): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${trimTo1dp(v / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${trimTo1dp(v / 1_000_000)}M`;
  if (abs >= 10_000) return `${trimTo1dp(v / 1_000)}K`;
  return v.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

/** A `unit` that names money rather than a suffix like `%` or `h`. */
const MONEY_UNIT = /^\s*(\$|usd|cad|aud|nzd|eur|gbp|£|€)\s*$/i;

/**
 * Label wording that means money even when the model forgot the unit. Kept narrow on
 * purpose: a false positive prints a `$` in front of a count, which is its own bug.
 */
const MONEY_LABEL = /(amount|requested|revenue|principal|balance|payout|disburse[md]?|\$)/i;

export function isMoneyUnit(unit?: string): boolean {
  return MONEY_UNIT.test(String(unit ?? ''));
}

/** Is this measure money? An explicit non-currency unit (`%`, `h`, `d`) always wins. */
export function isMoneyMeasure(unit?: string, label?: string): boolean {
  const u = String(unit ?? '').trim();
  if (u) return isMoneyUnit(u);
  return MONEY_LABEL.test(String(label ?? ''));
}

function moneySymbol(unit?: string): string {
  const u = String(unit ?? '').trim().toLowerCase();
  if (u === '£' || u === 'gbp') return '£';
  if (u === '€' || u === 'eur') return '€';
  return '$';
}

/**
 * Money is **never** compacted and always carries cents.
 *
 * `1.2M` next to prose reading `$1,233,100` is a reconciliation bug the operator has to
 * chase; `3,305.9` for an average is a figure they cannot paste into a ledger.
 */
export function money(n: number, unit?: string): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const body = Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${v < 0 ? '-' : ''}${moneySymbol(unit)}${body}`;
}

/**
 * The single formatter behind every mark label, tile value and table-twin cell: money
 * in full, everything else compacted only past a million, with the unit appended.
 */
export function formatMeasure(n: number, unit?: string, label?: string): string {
  if (isMoneyMeasure(unit, label)) return money(n, unit);
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `${compact(v)}${unit ?? ''}`;
}

/* ── Axis label geometry ──────────────────────────────────────────────────────
 * Hand-authored SVG has no layout engine, so a label's width has to be estimated
 * before it is placed. These are deliberate slight over-estimates: guessing wide
 * costs a few pixels of gutter, guessing narrow clips the text.
 */

/** Advance width of one character at 1px font size. */
function charEm(ch: string): number {
  if (/[ijltfr.,:;'`|![\]()]/.test(ch)) return 0.34;
  if (/[MW@%]/.test(ch)) return 0.95;
  if (/[A-Z0-9]/.test(ch)) return 0.66;
  if (/[a-z]/.test(ch)) return 0.56;
  if (/\s/.test(ch)) return 0.3;
  return 0.6;
}

/** Approximate rendered width of `s` in px. */
export function textWidth(s: string, fontSize: number): number {
  let em = 0;
  for (const ch of String(s ?? '')) em += charEm(ch);
  return em * fontSize;
}

/**
 * Trim a label to a pixel budget, **always from the end**.
 *
 * Losing the tail of `FORENSIC_PROOF_FAILED` still reads as a forensic failure; losing
 * the head leaves `RENSIC_PROOF_FAILED`, which names nothing. Callers pair this with a
 * `<title>` carrying the full string.
 */
export function fitText(s: string, fontSize: number, maxWidth: number): string {
  const full = String(s ?? '');
  if (!full || textWidth(full, fontSize) <= maxWidth) return full;
  const ellipsis = 0.6 * fontSize;
  let out = '';
  let w = 0;
  for (const ch of full) {
    const next = w + charEm(ch) * fontSize;
    if (next + ellipsis > maxWidth) break;
    out += ch;
    w = next;
  }
  return `${out.trimEnd()}…`;
}

/* ── Axis tick thinning ─────────────────────────────────────────────────────── */

/**
 * Draw every nth x label. **Marks are never thinned — only labels**: a 31-day series
 * keeps all 31 bars, it just stops printing 31 overlapping dates under them.
 */
export function tickStep(count: number, maxTicks = 10): number {
  if (!Number.isFinite(count) || count <= maxTicks) return 1;
  return Math.ceil(count / maxTicks);
}

/** First and last always survive, so the range still reads. */
export function isTickIndex(i: number, count: number, step: number): boolean {
  return i === 0 || i === count - 1 || i % step === 0;
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/;
const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Shorten a run of ISO dates to what actually varies: `7` inside one month, `Aug 7`
 * inside one year. Anything that is not a clean run of ISO dates is returned untouched —
 * guessing at a format the model invented is worse than a long label.
 */
export function shortenDateLabels(labels: string[]): string[] {
  if (!Array.isArray(labels) || labels.length < 2) return labels;
  const parsed = labels.map((l) => ISO_DAY.exec(String(l ?? '')));
  if (parsed.some((p) => !p)) return labels;
  const rows = parsed as RegExpExecArray[];
  const sameYear = rows.every((p) => p[1] === rows[0][1]);
  if (!sameYear) return labels;
  if (rows.every((p) => p[2] === rows[0][2])) return rows.map((p) => String(Number(p[3])));
  return rows.map((p) => `${MONTH_ABBR[Number(p[2]) - 1] ?? p[2]} ${Number(p[3])}`);
}

/** True when every meaningful value in a column is a number — blanks and dashes ignored. */
export function isNumericColumn(values: string[]): boolean {
  const meaningful = values
    .map((v) => String(v ?? '').trim())
    .filter((v) => v !== '' && v !== '-' && v !== '—');
  if (!meaningful.length) return false;
  return meaningful.every((v) => /^[-+]?[$]?\s?[\d,]+(\.\d+)?\s?%?$/.test(v));
}

/** Coerce an unknown JSON value to a finite number, defaulting to 0. */
export function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
